import { useEffect, useState, useCallback } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { CheckCircle2, XCircle, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function GiveResult({ cancelled = false }) {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [state, setState] = useState(cancelled ? "cancelled" : "checking");
  const [info, setInfo] = useState(null);

  const poll = useCallback(async (attempt) => {
    if (attempt > 8) return setState("timeout");
    try {
      const { data } = await axios.get(`${API}/payments/status/${sessionId}`);
      if (data.payment_status === "paid") {
        setInfo(data);
        return setState("paid");
      }
    } catch { /* ignore */ }
    setTimeout(() => poll(attempt + 1), 2000);
  }, [sessionId]);

  useEffect(() => {
    if (cancelled) return;
    if (!sessionId) return setState("timeout");
    poll(0);
  }, [cancelled, sessionId, poll]);

  return (
    <div className="min-h-screen flex items-center justify-center px-6 text-center" data-testid="give-result">
      <div className="max-w-lg">
        {state === "checking" && (
          <>
            <Loader2 className="animate-spin text-secondary mx-auto mb-6" size={40} />
            <h1 className="font-display uppercase text-4xl text-foreground">Confirming your gift…</h1>
          </>
        )}
        {state === "paid" && (
          <>
            <CheckCircle2 className="text-secondary mx-auto mb-6" size={48} data-testid="give-success-icon" />
            <p className="overline text-primary mb-3">Gift Received</p>
            <h1 className="font-display uppercase text-5xl text-foreground mb-4">Thank You</h1>
            <p className="font-editorial italic text-2xl text-muted-foreground">
              ${Number(info?.amount).toFixed(2)} sown into {info?.fund}. May it be multiplied.
            </p>
          </>
        )}
        {(state === "cancelled" || state === "timeout") && (
          <>
            <XCircle className="text-muted-foreground mx-auto mb-6" size={48} />
            <h1 className="font-display uppercase text-4xl text-foreground mb-4">
              {state === "cancelled" ? "Gift Cancelled" : "Still Processing"}
            </h1>
            <p className="font-body text-muted-foreground">
              {state === "cancelled" ? "No charge was made." : "Your payment may still complete shortly."}
            </p>
          </>
        )}
        <Link to="/give" className="inline-block mt-10 overline text-secondary border-b border-secondary pb-1">
          Return to Giving
        </Link>
      </div>
    </div>
  );
}
